import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Download,
  Fullscreen,
  FullscreenExit,
  ZoomIn,
  ZoomOut,
  Print,
  Share,
  Email
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Import resume templates
import ModernTemplate from '../templates/ModernTemplate';
import ClassicTemplate from '../templates/ClassicTemplate';
import CreativeTemplate from '../templates/CreativeTemplate';
import MinimalTemplate from '../templates/MinimalTemplate';
import ProfessionalTemplate from '../templates/ProfessionalTemplate';

const PreviewExportTab = ({
  candidateData,
  selectedTemplate,
  customizations,
  onNotification
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [paperSize, setPaperSize] = useState('a4');
  const [zoom, setZoom] = useState(100);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const resumeRef = useRef(null);

  const templates = {
    modern: ModernTemplate,
    classic: ClassicTemplate,
    creative: CreativeTemplate,
    minimal: MinimalTemplate,
    professional: ProfessionalTemplate
  };

  const SelectedTemplate = templates[selectedTemplate] || ModernTemplate;

  const exportToPDF = async () => {
    if (!resumeRef.current) return;
    
    setIsExporting(true);
    try {
      const canvas = await html2canvas(resumeRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: resumeRef.current.scrollWidth,
        height: resumeRef.current.scrollHeight
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: paperSize
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      const fileName = `${candidateData.name || 'Resume'}_${selectedTemplate}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      onNotification('PDF exported successfully!', 'success');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      onNotification('Failed to export PDF. Please try again.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const printResume = () => {
    const printWindow = window.open('', '_blank');
    const resumeHtml = resumeRef.current.innerHTML;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Resume - ${candidateData.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            @media print { body { margin: 0; } }
            * { box-sizing: border-box; }
          </style>
        </head>
        <body>
          ${resumeHtml}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const shareResume = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Resume - ${candidateData.name}`,
          text: `Check out ${candidateData.name}'s professional resume`,
          url: window.location.href
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback: copy URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      onNotification('Resume URL copied to clipboard!', 'success');
    }
  };

  const emailResume = () => {
    const subject = encodeURIComponent(`Resume - ${candidateData.name}`);
    const body = encodeURIComponent(`Please find attached my professional resume.\n\nBest regards,\n${candidateData.name}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleZoomChange = (event, newValue) => {
    setZoom(newValue);
  };

  const paperSizes = [
    { value: 'a4', label: 'A4 (210 × 297 mm)' },
    { value: 'letter', label: 'Letter (8.5 × 11 in)' },
    { value: 'legal', label: 'Legal (8.5 × 14 in)' },
    { value: 'a3', label: 'A3 (297 × 420 mm)' }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Controls */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Preview & Export
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Preview your resume and export it as PDF or print it directly.
        </Typography>

        {/* Control Panel */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Paper Size</InputLabel>
                <Select
                  value={paperSize}
                  label="Paper Size"
                  onChange={(e) => setPaperSize(e.target.value)}
                >
                  {paperSizes.map((size) => (
                    <MenuItem key={size.value} value={size.value}>
                      {size.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box sx={{ px: 2 }}>
                <Typography gutterBottom>Zoom: {zoom}%</Typography>
                <Slider
                  value={zoom}
                  onChange={handleZoomChange}
                  min={50}
                  max={200}
                  step={10}
                  valueLabelDisplay="auto"
                  size="small"
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip title="Export as PDF">
                  <Button
                    variant="contained"
                    startIcon={isExporting ? <CircularProgress size={20} /> : <Download />}
                    onClick={exportToPDF}
                    disabled={isExporting}
                  >
                    {isExporting ? 'Exporting...' : 'Export PDF'}
                  </Button>
                </Tooltip>

                <Tooltip title="Print Resume">
                  <IconButton onClick={printResume} color="primary">
                    <Print />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Share Resume">
                  <IconButton onClick={shareResume} color="primary">
                    <Share />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Email Resume">
                  <IconButton onClick={emailResume} color="primary">
                    <Email />
                  </IconButton>
                </Tooltip>

                <Tooltip title={showFullscreen ? "Exit Fullscreen" : "Fullscreen Preview"}>
                  <IconButton 
                    onClick={() => setShowFullscreen(!showFullscreen)} 
                    color="primary"
                  >
                    {showFullscreen ? <FullscreenExit /> : <Fullscreen />}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Zoom In">
                  <IconButton 
                    onClick={() => setZoom(Math.min(200, zoom + 10))} 
                    color="primary"
                  >
                    <ZoomIn />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Zoom Out">
                  <IconButton 
                    onClick={() => setZoom(Math.max(50, zoom - 10))} 
                    color="primary"
                  >
                    <ZoomOut />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Resume Preview */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          p: 2,
          borderRadius: 1,
          minHeight: '70vh'
        }}
      >
        <Box
          ref={resumeRef}
          sx={{
            transform: `scale(${zoom / 100})`,
            transformOrigin: 'top center',
            backgroundColor: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            borderRadius: 1,
            overflow: 'hidden',
            width: paperSize === 'a4' ? '210mm' : 
                   paperSize === 'letter' ? '8.5in' : 
                   paperSize === 'legal' ? '8.5in' : '297mm',
            minHeight: paperSize === 'a4' ? '297mm' : 
                      paperSize === 'letter' ? '11in' : 
                      paperSize === 'legal' ? '14in' : '420mm',
            transition: 'transform 0.3s ease'
          }}
        >
          <SelectedTemplate 
            candidateData={candidateData}
            resumeData={candidateData} // Add resumeData as an alias for candidateData
            customizations={customizations}
          />
        </Box>
      </Box>

      {/* Fullscreen Dialog */}
      <Dialog
        open={showFullscreen}
        onClose={() => setShowFullscreen(false)}
        maxWidth={false}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: '#f5f5f5'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Resume Preview - {candidateData.name}</Typography>
          <IconButton onClick={() => setShowFullscreen(false)}>
            <FullscreenExit />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Box
            sx={{
              backgroundColor: 'white',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              borderRadius: 1,
              overflow: 'hidden',
              width: paperSize === 'a4' ? '210mm' : 
                     paperSize === 'letter' ? '8.5in' : 
                     paperSize === 'legal' ? '8.5in' : '297mm',
              minHeight: paperSize === 'a4' ? '297mm' : 
                        paperSize === 'letter' ? '11in' : 
                        paperSize === 'legal' ? '14in' : '420mm'
            }}
          >
            <SelectedTemplate 
              candidateData={candidateData}
              resumeData={candidateData} // Add resumeData as an alias for candidateData
              customizations={customizations}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            variant="contained"
            startIcon={isExporting ? <CircularProgress size={20} /> : <Download />}
            onClick={exportToPDF}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export PDF'}
          </Button>
          <Button startIcon={<Print />} onClick={printResume}>
            Print
          </Button>
          <Button onClick={() => setShowFullscreen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Tips */}
      <Paper sx={{ mt: 3, p: 2, backgroundColor: '#f8f9fa' }}>
        <Typography variant="h6" gutterBottom>
          Export Tips
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" paragraph>
              <strong>PDF Export:</strong> High-quality PDF suitable for email attachments and online applications.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Print:</strong> Optimized for physical printing with proper margins and page breaks.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" paragraph>
              <strong>Sharing:</strong> Share your resume URL or copy the link to your clipboard.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Paper Size:</strong> Choose the appropriate paper size based on your region (A4 for international, Letter for US).
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default PreviewExportTab;